import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GripVertical, Plus, Pencil, Trash2, Check, X, ChevronDown, ChevronUp } from 'lucide-react'
import { motion, AnimatePresence, Reorder } from 'framer-motion'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'

interface QuestionOption {
  id: string
  label: string
  value: string
}

interface CustomQuestion {
  id: string
  type: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox'
  question: string
  required: boolean
  options?: QuestionOption[]
  order: number
}

interface CustomQuestionsManagerProps {
  jobId: string
  questions: CustomQuestion[]
  onUpdate?: () => void
}

const questionTypes = [
  { value: 'text', label: 'Short Answer' },
  { value: 'textarea', label: 'Long Answer' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Multiple Choice' },
  { value: 'checkbox', label: 'Checkboxes' },
]

export function CustomQuestionsManager({
  jobId,
  questions: initialQuestions,
  onUpdate,
}: CustomQuestionsManagerProps) {
  const [questions, setQuestions] = useState<CustomQuestion[]>(initialQuestions)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // New question state
  const [newQuestion, setNewQuestion] = useState<Partial<CustomQuestion>>({
    type: 'text',
    question: '',
    required: false,
    options: [],
  })

  const queryClient = useQueryClient()

  const updateQuestions = useMutation({
    mutationFn: (updatedQuestions: Partial<CustomQuestion>[]) =>
      api.put(`/jobs/${jobId}/questions`, { questions: updatedQuestions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      onUpdate?.()
      toast({ title: 'Questions updated', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to update questions', variant: 'destructive' })
    },
  })

  const addQuestion = useMutation({
    mutationFn: (data: Partial<CustomQuestion>) =>
      api.post(`/jobs/${jobId}/questions`, data),
    onSuccess: (newQ: any) => {
      setQuestions([...questions, newQ])
      setIsAddingNew(false)
      setNewQuestion({
        type: 'text',
        question: '',
        required: false,
        options: [],
      })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      toast({ title: 'Question added', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to add question', variant: 'destructive' })
    },
  })

  const deleteQuestion = useMutation({
    mutationFn: (questionId: string) =>
      api.delete(`/jobs/${jobId}/questions/${questionId}`),
    onSuccess: (_, questionId) => {
      setQuestions(questions.filter((q) => q.id !== questionId))
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      toast({ title: 'Question deleted', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to delete question', variant: 'destructive' })
    },
  })

  const handleReorder = (newOrder: CustomQuestion[]) => {
    const reordered = newOrder.map((question, index) => ({
      ...question,
      order: index,
    }))
    setQuestions(reordered)
    updateQuestions.mutate(
      reordered.map((q) => ({ id: q.id, order: q.order }))
    )
  }

  const handleAddQuestion = () => {
    if (!newQuestion.question?.trim()) return
    addQuestion.mutate({
      ...newQuestion,
      order: questions.length,
    })
  }

  const addOption = () => {
    const options = newQuestion.options || []
    setNewQuestion({
      ...newQuestion,
      options: [
        ...options,
        {
          id: `opt-${Date.now()}`,
          label: '',
          value: '',
        },
      ],
    })
  }

  const updateOption = (index: number, label: string) => {
    const options = [...(newQuestion.options || [])]
    options[index] = {
      ...options[index],
      label,
      value: label.toLowerCase().replace(/\s+/g, '-'),
    }
    setNewQuestion({ ...newQuestion, options })
  }

  const removeOption = (index: number) => {
    const options = [...(newQuestion.options || [])]
    options.splice(index, 1)
    setNewQuestion({ ...newQuestion, options })
  }

  const needsOptions = (type: string) => ['select', 'radio', 'checkbox'].includes(type)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Application Questions</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddingNew(true)}
          disabled={isAddingNew}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Question
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Add custom questions to collect additional information from applicants.
        </p>

        {questions.length === 0 && !isAddingNew ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No custom questions yet.</p>
            <Button
              variant="link"
              onClick={() => setIsAddingNew(true)}
              className="mt-2"
            >
              Add your first question
            </Button>
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={questions}
            onReorder={handleReorder}
            className="space-y-2"
          >
            <AnimatePresence mode="popLayout">
              {questions.map((question) => (
                <Reorder.Item
                  key={question.id}
                  value={question}
                  className="list-none"
                >
                  <motion.div
                    layout
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="border border-border rounded-lg bg-card"
                  >
                    <div
                      className="flex items-center gap-3 p-3 cursor-pointer"
                      onClick={() =>
                        setExpandedId(expandedId === question.id ? null : question.id)
                      }
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing" />

                      <div className="flex-1">
                        <p className="font-medium">{question.question}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {questionTypes.find((t) => t.value === question.type)?.label}
                          </span>
                          {question.required && (
                            <span className="text-xs text-destructive">Required</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteQuestion.mutate(question.id)
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                        {expandedId === question.id ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedId === question.id && question.options && question.options.length > 0 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-border"
                        >
                          <div className="p-3 space-y-1">
                            <p className="text-xs text-muted-foreground mb-2">Options:</p>
                            {question.options.map((opt, i) => (
                              <div key={opt.id} className="flex items-center gap-2 text-sm">
                                <span className="w-5 h-5 flex items-center justify-center text-xs text-muted-foreground">
                                  {i + 1}.
                                </span>
                                <span>{opt.label}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </Reorder.Item>
              ))}
            </AnimatePresence>
          </Reorder.Group>
        )}

        {/* Add new question form */}
        <AnimatePresence>
          {isAddingNew && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 border border-dashed border-border rounded-lg"
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Question</Label>
                  <Input
                    placeholder="e.g., What interests you about this role?"
                    value={newQuestion.question || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, question: e.target.value })
                    }
                    autoFocus
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Question Type</Label>
                    <Select
                      value={newQuestion.type}
                      onValueChange={(value: any) =>
                        setNewQuestion({
                          ...newQuestion,
                          type: value,
                          options: needsOptions(value) ? newQuestion.options || [] : undefined,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {questionTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="required"
                      checked={newQuestion.required}
                      onCheckedChange={(checked) =>
                        setNewQuestion({ ...newQuestion, required: checked })
                      }
                    />
                    <Label htmlFor="required">Required</Label>
                  </div>
                </div>

                {/* Options for select/radio/checkbox */}
                {needsOptions(newQuestion.type || '') && (
                  <div className="space-y-2">
                    <Label>Options</Label>
                    <div className="space-y-2">
                      {(newQuestion.options || []).map((opt, index) => (
                        <div key={opt.id} className="flex items-center gap-2">
                          <Input
                            placeholder={`Option ${index + 1}`}
                            value={opt.label}
                            onChange={(e) => updateOption(index, e.target.value)}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeOption(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addOption}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Option
                    </Button>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingNew(false)
                      setNewQuestion({
                        type: 'text',
                        question: '',
                        required: false,
                        options: [],
                      })
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddQuestion}
                    loading={addQuestion.isPending}
                    disabled={!newQuestion.question?.trim()}
                  >
                    Add Question
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
